import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Check,
  ClipboardList,
  Eye,
  Loader2,
  Lock,
  MapPin,
  MessageCircle,
  Save,
  Send,
} from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { containerLineDriverAPI } from "../../services/api";
import { DriverCmrTile } from "../../components/ContainerOrderForm/DriverCmrTile";
import { DriverMessagesTab } from "../../components/ContainerOrderForm/DriverMessagesTab";
import { DriverNextContainer } from "../../components/ContainerOrderForm/DriverNextContainer";
import { DriverLineAttachments } from "../../components/ContainerOrderForm/DriverLineAttachments";
import { DriverStopFrame } from "../../components/ContainerOrderForm/DriverStopFrame";
import { DriverRouteTimeline } from "../../components/ContainerOrderForm/DriverRouteTimeline";
import { DriverContainerDetails } from "../../components/ContainerOrderForm/DriverContainerDetails";
import { DriverCargoSection } from "../../components/ContainerOrderForm/DriverCargoSection";
import PlateCapture from "../../components/containerOrders/PlateCapture";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { getCountries } from "../../store/slices/companySlice";
import type {
  ContainerLineAttachment,
  ContainerLineStepPlan,
  ContainerOrderDirection,
  ContainerOrderLine,
} from "../../types/containerOrders";
import type {
  ContainerMessageThreadEvent,
  ContainerMessaging,
} from "../../types/containerMessages";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import { useKeyboardOpen } from "../../hooks/useVisualViewport";
import {
  completedStepCount,
  lineSteps,
  routeStopsOf,
} from "../../utils/containerLineSteps";
import {
  DRIVER_FILLABLE_REQUIREMENTS,
  blockingRequirements,
} from "../../utils/containerLineStart";
import { lockReason, nextContainerFor } from "../../utils/containerLineQueue";
import {
  deriveVgm,
  evaluateWeights,
} from "../../utils/containerLineCargo";
import {
  isRetryable,
  requestGeoFix,
  type GeoFailure,
} from "../../utils/driverGeolocation";

/**
 * The driver's execution screen for one container.
 *
 * Rebuilt around a single frame. What was here before showed the whole run at
 * once — a card per stop, a five-input details block, the packing list, the
 * documents — and everything on it was visible, which sounds like a virtue and
 * is not: a driver at a terminal gate is doing exactly one thing, and it was
 * reachable only by scrolling past the things they were not doing.
 *
 * Now the screen is that one thing. The stop being worked fills the frame with
 * its facts, its note, its two buttons and the panel of figures that unlocks
 * between them; the rest of the route is a collapsed timeline below it, and the
 * documents are below that. Three blocks, in the order they get used.
 *
 * Two rules underneath it are worth naming because they are what the shape is
 * for:
 *
 *   A step cannot be recorded without a location. Not "we try for five seconds
 *   and send it anyway" — which is what it used to be, and which is why the
 *   stamps that existed were the easy ones. See `utils/driverGeolocation`.
 *
 *   The box's figures are asked for at the first stop, not before it. A driver
 *   sent to collect a container cannot state its number before they have seen
 *   the door it is painted on. See `DriverContainerDetails`.
 */

type PlateReadingResult = Awaited<
  ReturnType<typeof containerLineDriverAPI.readPlate>
>["data"]["data"];

/** An empty box means "not stated", which is null — not zero. */
const numOrNull = (value: string) =>
  value.trim() === "" || Number.isNaN(Number(value)) ? null : Number(value);

/**
 * The signal reasons that mean this job is over, whatever the driver is doing.
 *
 * Kept as a set rather than a truthy check on `reason` because the great
 * majority of `truck-plan:changed` signals are about the rest of the day — a
 * slot moved, a container sent, the week reordered — and pulling the open
 * record for those is the behaviour this page deliberately does not have.
 *
 * Mirrors the reasons `announceContainerChange` raises on the server. A reason
 * added there and not here degrades to the old behaviour (the driver finds out
 * at the next save) rather than to anything worse.
 */
/**
 * What the tab bar is assumed to be until it has been measured.
 *
 * A starting value, not the answer. The bar's real height is its icon, its
 * label and its padding, and the label is type — so a driver who has turned
 * their system font up gets a taller bar than this, and one in a locale with
 * a two-line label taller still. Hard-coding it meant the page reserved 56px
 * for something that routinely drew 60, 70, more; the overflow went under the
 * bar, and the last block on the order tab — the one saying what comes after
 * this container — was what sat there.
 *
 * `useTabBarHeight` replaces this with the measured height on the first frame.
 */
const TAB_BAR_HEIGHT = 56;

/**
 * The tab bar's real height, as rendered.
 *
 * Two layouts depend on this number and they fail in different ways when it is
 * wrong: the order tab reserves it as padding, so an underestimate hides the
 * bottom of the page behind the bar; the messages tab subtracts it from a
 * fixed-height column, so an underestimate pushes the composer under it.
 *
 * Measured rather than computed. The height depends on the font the device is
 * actually rendering at, which nothing in this file can know — and the bar is
 * `fixed`, so its height never depends on the padding it produces and there is
 * no loop to guard against.
 *
 * Returns the fallback while the bar is unmounted (the keyboard is up and the
 * bar is gone), which costs nothing: the caller zeroes the reservation then.
 */
function useTabBarHeight(node: HTMLElement | null, enabled: boolean) {
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!enabled || !node) return;

    const measure = () => {
      const next = node.getBoundingClientRect().height;
      // Sub-pixel churn on a resize would re-render for nothing.
      if (next > 0) {
        setHeight((prev) =>
          prev !== null && Math.abs(prev - next) < 0.5 ? prev : next,
        );
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, enabled]);

  return height;
}

const CLOSING_REASONS = new Set([
  "line-cancelled",
  "line-removed",
  "order-cancelled",
]);

export default function DriverContainerExecutionPage() {
  const { lineId } = useParams<{ lineId: string }>();
  const { t } = useTranslation("containerOrders");
  const navigate = useNavigate();

  const [line, setLine] = useState<ContainerOrderLine | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Never true on desktop. Hides the tab bar while typing.
  const keyboardOpen = useKeyboardOpen();

  /**
   * Which half of the screen the driver is on.
   *
   * The container's run, or the conversation about it. Two tabs rather than one
   * long page because they are two different jobs: the stop frame is built
   * around a driver doing exactly one thing at a gate, and a chat below it
   * would be permanently on screen and permanently scrolled past. The badge on
   * the messages tab is what makes hiding it safe.
   *
   * Local to the screen and not in the URL: it is a view state, and a driver
   * who comes back to a container is coming back to the work.
   */
  const [tab, setTab] = useState<"order" | "messages">("order");

  /**
   * The one thing this screen needs from the parent order, and nothing else.
   *
   * The consignment note has two parties where a container run has three stops
   * or more, so something has to say which leg actually carries the goods —
   * that is the direction, and the API hands it over beside the container
   * rather than on it. Deliberately never rendered: a driver is given a
   * container, not a booking, and the order's own fields are not theirs to
   * read. It reaches exactly one place, which is the note.
   */
  const [direction, setDirection] = useState<ContainerOrderDirection | null>(
    null,
  );

  /**
   * The rest of the driver's day, so this screen can say what comes next.
   *
   * The same request the driver's list makes, reused rather than given an
   * endpoint of its own: a driver has a handful of active containers, the list
   * already exists, and "what is next" is a question about that list rather
   * than a new fact needing a new route. Fetched once on mount and refreshed
   * when this container finishes, which are the only two moments the answer can
   * have changed in a way this screen cares about.
   *
   * Failure is swallowed. The queue is a convenience at the bottom of a screen
   * whose job is recording steps, and a driver at a gate must not be shown an
   * error about a card they had not asked for.
   */
  const [queue, setQueue] = useState<ContainerOrderLine[]>([]);

  /**
   * The container just finished, held so the sheet can name it.
   *
   * Set only when a step recording actually completes the run — never derived
   * from the status on render, or it would raise itself again every time a
   * finished container is reopened for review.
   */
  const [justFinished, setJustFinished] = useState(false);

  const authUser = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();

  /**
   * The country list, for the note's address boxes.
   *
   * A stop stores a country id and the note prints a name, so without this the
   * consignor and consignee come out with blank countries on an otherwise
   * complete document. Fetched the same way the shipment driver view fetches it
   * — the endpoint has always been open to drivers — and cached in the store,
   * so a driver who has already opened a job pays nothing for the second.
   */
  const countries = useAppSelector((state) => state.company.countries);

  useEffect(() => {
    if (!countries?.length) dispatch(getCountries());
  }, [countries?.length, dispatch]);

  const loadQueue = useCallback(async () => {
    try {
      const res = await containerLineDriverAPI.getMine("active");
      setQueue((res.data.data.lines ?? []) as ContainerOrderLine[]);
    } catch {
      // A card the driver did not ask for is not worth a message they did not
      // ask for either. It simply does not appear.
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  /**
   * The step waiting on a location, and why the phone could not supply one.
   *
   * Held rather than thrown away, because it is the whole of the driver's way
   * forward: a retry needs to know which step was being confirmed, and the
   * notify-dispatch request needs to say which of the three failures it was.
   */
  const [tabBar, setTabBar] = useState<HTMLElement | null>(null);
  // The bar is unmounted while the keyboard is up, so there is nothing to
  // measure then — and nothing to reserve either.
  const tabBarHeight = useTabBarHeight(tabBar, !keyboardOpen);
  const barReservation = keyboardOpen
    ? "0px"
    : tabBarHeight !== null
      ? `${tabBarHeight}px`
      : `calc(${TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`;

  const [geoBlock, setGeoBlock] = useState<{
    step: ContainerLineStepPlan;
    reason: GeoFailure;
  } | null>(null);
  const [notifying, setNotifying] = useState(false);

  const [capturing, setCapturing] = useState(false);
  const [reading, setReading] = useState<PlateReadingResult | null>(null);
  const [readingBusy, setReadingBusy] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);

  /**
   * Documents are their own list: uploads land here without a form save.
   *
   * The container's version token moves with them, because filing or removing a
   * document *is* a save. Dropping it left this screen holding a stale token,
   * and the next attempt to save the container's details was refused as a lost
   * update — telling the driver somebody else had changed the container when
   * the only somebody was them, one tap earlier.
   */
  const setAttachments = (
    attachments: ContainerLineAttachment[],
    updatedAt?: string,
  ) =>
    setLine((current) =>
      current
        ? { ...current, attachments, ...(updatedAt ? { updatedAt } : {}) }
        : current,
    );

  useEffect(() => {
    if (!lineId) return;
    let active = true;

    (async () => {
      try {
        const res = await containerLineDriverAPI.getOne(lineId);
        if (active) {
          setLine(res.data.data.line);
          setDirection(res.data.data.order?.direction ?? null);
        }
      } catch (e: any) {
        if (active) {
          toast.error(e.response?.data?.message || t("driver.loadFailed"));
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [lineId, t]);

  /** A new photograph is a new set of values; anything typed against the last is not an opinion about this one. */
  useEffect(() => {
    setEdits({});
  }, [reading]);

  const refresh = useCallback(async (): Promise<ContainerOrderLine | null> => {
    if (!lineId) return null;

    /*
     * Handed back as well as stored, because a caller that has just been
     * refused a save needs to know what the container turned out to be before
     * it can say anything useful about it — "press save again" and "this
     * container was cancelled" are both answers to a 409, and only the fresh
     * record says which. Reading it out of state instead would read the value
     * from before this update.
     */
    let fetched: ContainerOrderLine | null = null;
    try {
      const fresh = await containerLineDriverAPI.getOne(lineId);
      fetched = fresh.data.data.line as ContainerOrderLine;
      setLine(fetched);
    } catch {
      // The caller has already said what went wrong; inventing a second
      // failure on top of it helps nobody.
    }
    /**
     * And the rest of the day, on the same trip.
     *
     * This runs when the office has been found to have changed something under
     * the driver, which is the same minute they are most likely to have
     * changed the *queue* — a reshuffle at eleven o'clock moves several
     * containers, not one. The queue was otherwise fetched twice in the life of
     * this screen, on mount and on completion, so a container handed to
     * somebody else mid-shift went on being offered as "next" until the driver
     * happened to reopen the page.
     *
     * Not awaited by anything and quietly swallowed, like every other fetch of
     * it: a card at the bottom of the screen must not delay putting the
     * corrected route in front of a driver standing at a gate.
     */
    loadQueue();

    return fetched;
  }, [lineId, loadQueue]);

  /** The container's own counters, kept current without refetching it. */
  const applyMessaging = useCallback((messaging: ContainerMessaging) => {
    setLine((current) => (current ? { ...current, messaging } : current));
  }, []);

  /**
   * Messages arriving on containers this truck is carrying.
   *
   * This is the event the whole routing design exists for. The server sends the
   * *words* only into the room of the container they are about — which this
   * screen joins, through `DriverMessagesTab`, and only while the messages tab
   * is open. What it sends into the truck's own room is this: a counter, for
   * every container on the truck.
   *
   * So the two cases split cleanly, which is exactly what a driver wants:
   *
   *   about THIS container   the badge on the tab moves. If the tab is open the
   *                          message itself has already arrived by the other
   *                          route and is on screen; if it is not, the badge is
   *                          the whole of the news, and no message body was put
   *                          on this device to deliver it.
   *   about ANOTHER one      a toast naming that container, which opens it. The
   *                          message stays where it belongs — it is not
   *                          fetched, not held, and not shown here, because the
   *                          driver is at a gate working a different box.
   *
   * The driver's own messages come back through this room too (the office's
   * lists need the same counters), hence the two conditions: something new,
   * from the office, or there is nothing to say.
   */
  /**
   * Dispatch changed this truck's work while the driver is looking at a job.
   *
   * The queue is always refetched: the thing that most often changes underneath
   * this screen is the *rest* of the day — a job added behind this one, one
   * pulled back, the order rearranged — and that is exactly what the "next
   * container" panel reads.
   *
   * The open container is deliberately *not* refetched for those. The driver may
   * be halfway through typing a container number or a tare, and reloading
   * underneath them would either discard it or fight their edits. The form's own
   * concurrency token refuses a save made against a container somebody else
   * moved, which is the honest place to find that out.
   *
   * ── The exception, and why it is not optional ───────────────────────────────
   *
   * That reasoning rested on a premise that is no longer true. It used to be
   * that the board could do almost nothing to a container being executed — a
   * started run refused to be moved, unassigned or un-sent — so nothing the
   * office did could invalidate the screen in front of the driver.
   *
   * A started container can now be cancelled, by design: calling off one box a
   * truck is already carrying is an ordinary thing to have to do. Which means
   * the office can now close the job this screen is executing, and without this
   * branch the driver would carry on filling in a cancelled container until a
   * step was refused at the gate — the one place and moment where discovering it
   * is most expensive.
   *
   * So a signal that names *this* container, for a reason that closes it, pulls
   * the record. The banner and the disabled controls follow from `line.status`,
   * so refreshing is the whole of the fix. Scoped to that: any other reason
   * leaves the form alone exactly as before, and a signal about a different
   * container never touches it.
   */
  useSocketEvent<{ lineId?: string; reason?: string }>(
    "truck-plan:changed",
    (payload) => {
      void loadQueue();

      if (payload?.lineId && payload.lineId !== lineId) return;
      if (!payload?.reason || !CLOSING_REASONS.has(payload.reason)) return;

      void refresh();
    },
  );

  useSocketEvent<ContainerMessageThreadEvent>(
    "containerMessage:thread",
    (payload) => {
      if (!payload?.lineId) return;

      if (payload.lineId === lineId) {
        applyMessaging(payload.messaging ?? {});
        return;
      }

      const messaging = payload.messaging ?? {};
      if (
        messaging.lastMessageFrom !== "office" ||
        !(messaging.unreadForDriver ?? 0)
      ) {
        return;
      }

      toast.info(
        t("messages.otherContainerToast", {
          container: payload.lineNumber ?? "",
        }),
        { onClick: () => navigate(`/driver/containers/${payload.lineId}`) },
      );
    },
  );

  /**
   * Record one step, once there is a location to record it at.
   *
   * The fix is acquired *before* the request and the request is not made
   * without one — the server refuses it anyway, and finding that out from a 422
   * after a round trip is a worse experience than being told up front. A
   * failure raises the panel below rather than a toast, because a toast
   * disappears and this needs an answer.
   */
  const recordStep = async (step: ContainerLineStepPlan) => {
    if (!lineId || busy) return;
    setBusy(true);
    setGeoBlock(null);

    try {
      const located = await requestGeoFix();

      if (!located.ok) {
        setGeoBlock({ step, reason: located.reason });
        return;
      }

      const res = await containerLineDriverAPI.step(lineId, {
        step: step.step,
        // Which stop, as well as which step. A route can hold several added
        // stops and they all call their halves `arrived_stop`, so the name on
        // its own cannot say which confirmation this is.
        stopId: step.stopId,
        // And which *version* of the route this screen is showing. Dispatch can
        // move a stop while the phone is open, and the server has no other way
        // to know the driver is confirming something they were shown that is no
        // longer true.
        updatedAt: line?.updatedAt,
        ...located.fix,
      });

      const saved = res.data.data.line as ContainerOrderLine;
      setLine(saved);
      toast.success(t(`linesBlock.steps.${step.step}`));

      /**
       * The last step of a run is the one moment worth interrupting for.
       *
       * A driver who has just recorded a drop-off is deciding what to do with
       * the rest of their day, and the answer was two screens away — back out
       * to the list, read it, pick. So the screen offers it: what is next, or
       * that there is nothing next, which is equally worth being told.
       *
       * Raised from what the *server* returned rather than from a local count,
       * because the server is what decides the run is over — a route that
       * changed underneath the driver could make this step the last one when
       * the screen still thought otherwise.
       *
       * The queue is refetched first so the sheet cannot offer the container
       * that was just completed: it drops out of the `active` scope, and a
       * stale list would still be holding it.
       */
      if (saved.status === "completed") {
        await loadQueue();
        setJustFinished(true);
      }
    } catch (e: any) {
      const status = e.response?.status;
      toast.error(e.response?.data?.message || t("driver.stepFailed"));

      /**
       * A refused step means this screen is out of date, so it is refreshed
       * rather than left showing the route the driver just failed to record
       * against. 409 is the office having moved the route underneath them; 400
       * is a step that is not on this route at all, which is the same situation
       * seen from the other side. Asking a driver at a gate to work out that
       * they should pull to refresh is asking them to debug.
       */
      if (status === 409 || status === 400) {
        await refresh();
        toast.info(t("driver.routeChanged"));
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Tell the office this step cannot be geo-stamped.
   *
   * Records nothing on the container, deliberately — if it did, it would *be*
   * the override it exists to avoid. The office records the step from the
   * container's own form, where the event is stamped `source: "system"` with a
   * reason code and their name against it: a worse record than a geo-stamp and
   * a far better one than a silent blank, because somebody signed it.
   */
  const notifyDispatch = async () => {
    if (!lineId || !geoBlock || notifying) return;
    setNotifying(true);
    try {
      const res = await containerLineDriverAPI.reportGeoBlocked(lineId, {
        reason: geoBlock.reason,
      });
      toast.success(res.data.message || t("driver.geo.notified"));
      setGeoBlock(null);
    } catch (e: any) {
      toast.error(e.response?.data?.message || t("driver.geo.notifyFailed"));
    } finally {
      setNotifying(false);
    }
  };

  /**
   * A save the server refused because the container moved underneath it.
   *
   * The lost-update guard is right to exist — dispatch and the driver write to
   * the same six fields, and whoever saved second used to win silently — but
   * the way it landed on this screen was a dead end. The container's version
   * token is only refreshed by a save or by the step recorder; nothing on the
   * execution screen listens for the office editing a container. So a
   * dispatcher adding a note while a driver stood at a weighbridge left the
   * phone holding a token that could never become current again: Save answered
   * "changed by someone else, please reload", and pressing it again answered
   * the same thing, for as long as the screen stayed open. The only way out was
   * to leave the page and retype everything — at a gate, in gloves.
   *
   * "Reload" is also not an instruction a phone has an obvious answer to, which
   * is the point `recordStep` already makes about its own 409: asking a driver
   * at a gate to work out that they should pull to refresh is asking them to
   * debug.
   *
   * So the container is refetched here, which makes the token current, and the
   * driver is told what happened and asked to press save again. Deliberately
   * *not* an automatic retry: that would clobber whatever the office had just
   * written without anybody being told, which is the exact failure the guard
   * was added to prevent. One extra press, with an explanation, is the whole
   * cost — and the form keeps what was typed, because both panels refuse to
   * re-seed while they are open.
   *
   * Returns whether it handled the error, so each caller still reports anything
   * else in its own words.
   */
  const handledStaleSave = async (error: {
    response?: { status?: number };
  }): Promise<boolean> => {
    if (error?.response?.status !== 409) return false;

    const fresh = await refresh();

    /*
     * Two quite different things arrive as 409 on this endpoint, and telling a
     * driver the wrong one is worse than saying nothing. "Someone changed this,
     * press save again" is right for a version clash and actively misleading
     * for a container the office has just cancelled — where there is no save to
     * press and the panel is about to close itself. The refreshed record is
     * what distinguishes them, so the server's own words are used whenever it
     * is not simply a stale token.
     */
    if (fresh && (fresh.status === "cancelled" || fresh.status === "completed")) {
      return false;
    }

    toast.info(t("driver.versionMoved"));
    return true;
  };

  /**
   * Write the packing list, from the driver's own rows.
   *
   * Its own call rather than a field on `saveDetails`, because the two are
   * separate acts with separate buttons: a driver correcting a tare at a
   * terminal must not also be resending a packing list they have not looked at,
   * and a driver taking a pallet off the list must not be resending five
   * weights. One save, one subject.
   *
   * The version token rides along for the same reason it does there — both
   * sides write this list, and the office editing it while a phone holds the
   * old copy is exactly the lost update the guard exists for. A driver whose
   * save is refused as stale is told, which is the point: the message names
   * somebody else's change rather than silently discarding theirs.
   */
  const saveCargo = async (
    items: Array<{
      _id?: string;
      quantity: number | null;
      type: string;
      weightKg: number | null;
      description: string;
    }>,
  ): Promise<boolean> => {
    if (!lineId) return false;
    setBusy(true);
    try {
      const res = await containerLineDriverAPI.saveDetails(lineId, {
        updatedAt: line?.updatedAt,
        cargoItems: items,
      });
      setLine(res.data.data.line);
      toast.success(t("driver.cargoSaved"));
      return true;
    } catch (e: any) {
      if (!(await handledStaleSave(e))) {
        toast.error(e.response?.data?.message || t("toasts.lineFailed"));
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** Write the container's details, from explicit values. */
  const saveDetails = async (values: {
    containerNumber: string;
    sealNumber: string;
    mrnNumber: string;
    tareKg: string;
    maxPayloadKg: string;
    cargoWeightKg: string;
  }): Promise<boolean> => {
    if (!lineId) return false;
    setBusy(true);
    try {
      const res = await containerLineDriverAPI.saveDetails(lineId, {
        // The lost-update guard. Both sides write these fields, so this is
        // exactly where one save can quietly overwrite another.
        updatedAt: line?.updatedAt,
        containerNumber: values.containerNumber,
        sealNumber: values.sealNumber,
        // The customs MRN, which this screen may write for the whole run — the
        // declaration is often issued at an office partway along the route.
        mrnNumber: values.mrnNumber,
        // No VGM: it is derived from the tare below and the packing list, and
        // the API ignores anything sent for it.
        tareKg: numOrNull(values.tareKg),
        maxPayloadKg: numOrNull(values.maxPayloadKg),
        // Blank is `null`, which clears the declaration and hands the figure
        // back to the packing list — not a load of nothing. A typed 0 is.
        cargoWeightKg: numOrNull(values.cargoWeightKg),
        plateReadingId: reading?.readingId ?? null,
      });
      setLine(res.data.data.line);

      /**
       * Saving details saves details. It files nothing.
       *
       * The photograph a reading came from used to be uploaded here, as a
       * second request behind the first, so pressing Save put a document on the
       * container that nobody had asked to file. It surprised drivers — the
       * button says it saves the boxes above it — and it filed a photograph on
       * every reading whether it was worth keeping or not, on a phone
       * connection, at a gate.
       *
       * Nothing is lost that anybody chose: a driver who wants the plate on the
       * record photographs it into Documents below, which is an upload they
       * asked for and can see the result of. This screen's job is the figures.
       */
      setReading(null);
      toast.success(t("driver.detailsSaved"));
      return true;
    } catch (e: any) {
      if (!(await handledStaleSave(e))) {
        toast.error(e.response?.data?.message || t("toasts.lineFailed"));
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Send a burst to be read, and put the answer on screen.
   *
   * Nothing is saved by this. The reading is a *suggestion*, and the save that
   * follows is the driver's own act, made through the same endpoint they would
   * have used had they typed the numbers by hand — so nothing reaches the
   * container that a person did not put there.
   */
  const readPlate = async (frames: File[]) => {
    if (!lineId) return;
    setReadingBusy(true);
    try {
      const res = await containerLineDriverAPI.readPlate(lineId, frames);
      const result = res.data.data;
      setReading(result);
      // The frames themselves are not kept. The reading endpoint stores
      // nothing, and this screen no longer files a photograph behind the
      // driver's back — see `saveDetails`. A plate worth keeping is uploaded
      // through Documents, deliberately.
      setCapturing(false);

      if (result.verdict === "red") {
        toast.error(result.errors[0]?.message ?? t("plate.unreadable"));
      } else {
        toast.info(t("plate.checkAndImport"));
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message ?? t("plate.failed"));
      setCapturing(false);
    } finally {
      setReadingBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="driver-ui p-safe flex min-h-full items-center justify-center bg-[#F7F6F2]">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!line) {
    return (
      <div className="driver-ui p-safe flex min-h-full items-center justify-center bg-[#F7F6F2]">
        <p className="text-sm text-[#A6A49C]">{t("driver.notFound")}</p>
      </div>
    );
  }

  // ── Where the run stands ────────────────────────────────────────────────
  //
  // All of it read off the container's own route, never off a fixed table: two
  // containers on the same order can legitimately have different runs.
  const stops = routeStopsOf(line);
  const steps = lineSteps(line);
  const done = completedStepCount(line);
  const nextStep = steps[done] ?? null;
  const finished = done >= steps.length;
  const completed = line.status === "completed";

  const currentStopIndex = nextStep?.stopIndex ?? -1;
  const currentStop = currentStopIndex >= 0 ? stops[currentStopIndex] : null;

  /** 1-based position of the frame's stop among the ones actually being made. */
  const position =
    stops
      .slice(0, Math.max(currentStopIndex, 0))
      .filter((stop) => stop.enabled !== false).length + 1;

  /**
   * Whether the driver has reported in here.
   *
   * Read off the stop's own stamp rather than off the step count, because that
   * is the fact the frame is drawing — a stop with an `arrivedAt` has been
   * reported at, whatever else has happened since.
   */
  const arrived = !!currentStop?.arrivedAt;

  /**
   * What the run is blocked on *right now*, given how far it has got.
   *
   * Before the first step it is the arrival gate — the driver and the truck,
   * neither of which the driver can supply. Between the first and the second it
   * is the departure gate: the box's own figures, which is exactly when the
   * driver is standing in front of the box. After that, nothing; a run under
   * way is allowed to finish whatever happens behind the driver's back, which
   * is the same latitude the server gives it.
   */
  const missing = blockingRequirements(line, done);
  const blockedBy = missing.map((key) => t(`driver.requirements.${key}`));
  const fillableHere = missing.some((key) =>
    DRIVER_FILLABLE_REQUIREMENTS.includes(key),
  );

  const detailsUnlocked = arrived || done > 1;

  /**
   * Whether this container is the driver's to work yet, or one further down the
   * day reached by a link.
   *
   * The list no longer opens an out-of-turn container, which closes the way a
   * driver would ordinarily get here — but not the only way. A link from
   * earlier in the shift, the browser's back button after a finished run, a
   * card tapped in the instant before dispatch reordered the day: all three
   * land on this screen with a fully working stop frame on it, and the lock on
   * the list would have been a suggestion the screen ignored.
   *
   * The same function the cards use, from the same list, so the two cannot
   * disagree about which box is in the way.
   *
   * Fails *open*. The queue is fetched separately and may be empty — the
   * request has not landed, or it failed in a yard with no signal — and
   * `lockReason` answers null for a container it cannot see in the list. That
   * is the right way round: an unreachable list must not be able to stop a
   * driver working the box in front of them, which is the one thing a lock
   * meant to keep the day in order must never do.
   */
  const queueLock = lockReason(line, queue);

  /**
   * Whether the working frame is on screen at all.
   *
   * Three states collapse into one question. A finished or cancelled container
   * has no stop left to work; a locked one has a stop, but not this driver's
   * to work yet. All three fall through to the record panel below, which is
   * the read-only half of this screen — so a locked container shows every
   * figure on the box and not one control that would record anything.
   *
   * Kept as a value rather than repeated, because the attachments block
   * downstream switches on the *exact* same condition: anything looser and the
   * document list appears twice on one screen.
   */
  const frameShown = !queueLock && !!(currentStop && nextStep);

  /**
   * The container is a record rather than a form.
   *
   * Four ways in, and `finished` is the one that is easy to leave out. A run
   * with every step stamped is over whether or not the stored status has caught
   * up — the two are a derivation apart, and a screen reloaded in that gap would
   * otherwise offer an editable packing list over a container the API will
   * refuse to write. The record block below already draws the figures read-only
   * for exactly this reason; this puts the cargo table under the same rule
   * rather than relying on the status word arriving in time.
   *
   * Documents are deliberately *not* governed by this: the last receipt of a
   * run is routinely handed over at the drop-off gate, after the final step is
   * recorded. That block tests cancellation and the queue lock on its own.
   */
  const readOnly =
    completed || finished || line.status === "cancelled" || !!queueLock;
  // The box's identity closes with the first step: its number is what the
  // terminal paperwork was filed against.
  /**
   * The box's identity settles when the driver leaves the first stop, not when
   * they arrive at it.
   *
   * `done > 1`, not `done > 0`. The number is painted on a door the driver
   * reaches at the first stop, so a run that froze it on arrival froze it one
   * step before the only moment it could be read — and the departure gate then
   * demanded it. The API moved with this; see `hasLeftFirstStop`.
   *
   * A number that was never entered is not frozen either way. That can only
   * happen on a container from before these rules, and leaving it unfillable
   * would make a gap in the record permanent.
   */
  const numberFrozen = done > 1 && !!line.containerNumber;

  // ── The plate reading, as a form ────────────────────────────────────────
  //
  // Every row is prefilled from the photograph where it read something and from
  // the container where it did not, so what is on screen is exactly what Save
  // writes. No VGM row: it is derived, so there is nothing here for a
  // photograph to propose and nothing for the driver to accept.
  /**
   * The packing list panel, built once and placed twice.
   *
   * It belongs in two different containers depending on the state of the run —
   * inside the stop frame while there is a stop to work, inside the record
   * block when there is not — and both need exactly the same panel. Building
   * the element here rather than writing it twice is what stops the two copies
   * acquiring different props, which is how one of them would quietly end up
   * editable on a finished container.
   */
  const cargoPanel = (
    <DriverCargoSection
      line={line}
      readOnly={readOnly}
      busy={busy}
      onSave={saveCargo}
    />
  );

  const plateRows = reading
    ? [
        ...(numberFrozen
          ? []
          : [
              {
                field: "containerNumber",
                label: t("driver.containerNumber"),
                value:
                  reading.containerNumber.suggestions[0] ??
                  reading.containerNumber.value ??
                  line.containerNumber ??
                  "",
                fromPhoto: Boolean(reading.containerNumber.value),
                numeric: false,
              },
            ]),
        ...(
          [
            ["tareKg", "driver.tare", line.tareKg],
            ["maxPayloadKg", "driver.maxPayload", line.maxPayloadKg],
          ] as const
        ).map(([field, label, current]) => {
          const read = reading.weights[field];
          return {
            field,
            label: t(label),
            value: read != null ? String(read) : current != null ? String(current) : "",
            fromPhoto: read != null,
            numeric: true,
          };
        }),
      ]
    : [];

  const valueOf = (row: { field: string; value: string }) =>
    edits[row.field] ?? row.value ?? "";

  /**
   * What the whole set breaks, checked against the same rules the API enforces.
   *
   * Run over the container as the import would leave it rather than over the
   * rows that changed: a tare imported onto a packing list it cannot cover is a
   * collision between one figure from the photograph and one that was already
   * here, and a check that only looked at the new figures would never see it.
   */
  const importDraft = {
    containerNumber: valueOf(
      plateRows.find((r) => r.field === "containerNumber") ?? {
        field: "containerNumber",
        value: line.containerNumber ?? "",
      },
    ),
    tareKg: valueOf(
      plateRows.find((r) => r.field === "tareKg") ?? {
        field: "tareKg",
        value: "",
      },
    ),
    maxPayloadKg: valueOf(
      plateRows.find((r) => r.field === "maxPayloadKg") ?? {
        field: "maxPayloadKg",
        value: "",
      },
    ),
  };

  const importListedKg = (line.cargoItems ?? []).reduce(
    (total, item) => total + (item.weightKg ?? 0),
    0,
  );
  const importTare = numOrNull(importDraft.tareKg);
  const importConflicts = reading
    ? evaluateWeights({
        tareKg: importTare,
        maxPayloadKg: numOrNull(importDraft.maxPayloadKg),
        // The sum the save will store, so the panel cannot accept a set the
        // save then refuses. One shared derivation, so this preview cannot
        // disagree with the panel it is about to write into.
        vgmKg: deriveVgm({
          tareKg: importTare,
          declaredCargoKg: line.cargoWeightKg ?? null,
          listedKg: importListedKg,
        }),
        cargoKg: importListedKg,
        declaredCargoKg: line.cargoWeightKg ?? null,
      }).errors
    : [];

  const importBlocked = importConflicts.length > 0 || importing || busy;

  const importReading = async () => {
    if (!reading || importBlocked) return;
    setImporting(true);
    const ok = await saveDetails({
      containerNumber: importDraft.containerNumber,
      sealNumber: line.sealNumber ?? "",
      // Resent unchanged. A door plate says nothing about a customs
      // declaration, so this import has no opinion on the MRN — and a save that
      // omitted it would express one, by clearing it.
      mrnNumber: line.mrnNumber ?? "",
      tareKg: importDraft.tareKg,
      maxPayloadKg: importDraft.maxPayloadKg,
      // Resent unchanged, like the MRN: a door plate carries the tare and the
      // payload rating and says nothing about what has been loaded into the box.
      cargoWeightKg:
        line.cargoWeightKg != null ? String(line.cargoWeightKg) : "",
    });
    setImporting(false);
    if (ok) setEdits({});
  };

  return (
    <div
      className={`driver-ui bg-[#F7F6F2] ${
        /*
         * Two page shapes, because the tabs want opposite things.
         *
         * The order tab scrolls and needs the tab bar's height *plus* the home
         * indicator cleared beneath it — `pb-24` covered only the bar, which is
         * what trimmed the last card.
         *
         * The messages tab is a column exactly the height of the app shell, so
         * its composer sits at the bottom of the screen. The shell is pinned to
         * the part of the page actually visible (AppLayout), which on a phone
         * with the keyboard up is the part above the keyboard — so this needs
         * no keyboard maths of its own. The bar's space is reserved only while
         * the keyboard is down: holding it open with the keyboard up would push
         * the composer back under it.
         */
        tab === "order" ? "min-h-full" : "flex h-full flex-col overflow-hidden"
      }`}
      style={{
        /*
         * Both tabs reserve the bar the same way, as a plain inline padding.
         *
         * This used to go through the `pb-safe-bar` class on the order tab and
         * an inline style on the messages tab — two routes to one number, and
         * only one of them was ever observed working on a handset. Whatever the
         * class was doing differently, there was no reason for the difference:
         * it is the same measurement, reserved against the same bar, and one
         * path is one thing to get wrong instead of two.
         *
         * `barReservation` is already "0px" when the keyboard is up, because
         * the bar unmounts then — so this needs no condition of its own.
         */
        paddingBottom: barReservation,
      }}
    >
      {/* ── Which container ──────────────────────────────────────────────── */}
      <div className="pt-safe border-b border-[#E4E2DA] bg-white">
        <div className="mx-auto flex max-w-md items-center gap-3 px-3 py-3">
          <button
            type="button"
            // Back to the tab this container actually lives on, so a driver
            // reviewing finished work does not land on their to-do list.
            onClick={() =>
              navigate("/driver/containers", {
                state: { tab: completed ? "completed" : "active" },
              })
            }
            aria-label={t("driver.back")}
            /*
             * 44px square, which is the number rather than a guess: it is the
             * minimum touch target both platform guidelines name, and it is
             * what this control was missing. A 20px arrow in 8px of padding is
             * 36px — small enough that a gloved thumb in a moving cab misses
             * it, and the miss lands on the container number beside it, which
             * does nothing. So the arrow goes up to 24px and the box to 44,
             * and it gains a visible edge: a bordered square reads as a button
             * at arm's length where a bare glyph on white reads as decoration.
             *
             * `shrink-0` because the title beside it is `min-w-0` and truncates
             * — without it a long container number would be allowed to squeeze
             * the back button narrower than the whole point of this change.
             */
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#E4E2DA] bg-white active:bg-[#F0EEE8]"
          >
            <ArrowLeft className="h-6 w-6 text-[#44433E]" />
          </button>
          <div className="min-w-0">
            <div className="truncate font-semibold text-[#1A1A18]">
              {line.lineNumber}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-[#A6A49C]">
              <span>
                {done}/{steps.length} ·{" "}
                {t(`linesBlock.statuses.${line.status}`)}
              </span>
              {completed && (
                <span className="inline-flex items-center gap-1 rounded bg-[#E9F3DC] px-1.5 py-0.5 text-[11px] font-medium text-[#3B6D11]">
                  <Eye className="h-3 w-3" />
                  {t("lineEdit.readOnlyBadge")}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Phone width, capped ────────────────────────────────────────────
          448px, and the number is chosen rather than inherited. Real phone
          viewports run 360 through 430, so this cap is above every one of them
          — no actual device is ever narrowed by it, and it only does work on a
          tablet or a laptop, where it holds the phone shape instead of letting
          the page stretch.

          Stretching was the problem it fixes. The fact rows in the stop frame
          are `justify-between`, so at the 672px this used to be, "Order nr" and
          its value sat some five hundred pixels apart and the eye had to walk an
          empty line to pair them. That reads correctly at phone width and badly
          anywhere wider.

          Not narrower, either. Three regions here are two columns — the two
          buttons, the tare/payload inputs, and the four document tiles — and at
          400px those tiles are 85px squares carrying an icon, a filename and a
          badge. The forty-eight pixels are the difference between legible and
          cramped.

          Matched by the header bar above, padding included, so the back arrow
          lines up with the cards beneath it. */}
      {tab === "order" && (
      <div className="mx-auto max-w-md space-y-3 px-3 py-4">
        {/* ── Called off ───────────────────────────────────────────────────
            A cancelled container leaves every control on this screen greyed
            and said nothing about why. The status word in the header bar was
            the whole of the notice, and it is four grey characters beside a
            step count — so what the driver actually saw was a confirm button
            that would not press, which is indistinguishable from an app that
            has stopped working. This is the one thing that has to be read
            before anything else on the screen, so it sits above the frame.

            Reachable in exactly two ways, and both are ordinary: dispatch
            cancels while the screen is open, or the driver returns to a link
            from earlier in the shift. A cancelled container is in neither of
            their tabs, so there is nowhere else this could have been said. */}
        {line.status === "cancelled" && (
          <div className="rounded-[14px] border border-[#E8B4B3] bg-[#FBEDED] px-3 py-3">
            <p className="flex items-center gap-2 text-[13px] font-semibold text-[#A63A39]">
              <Ban className="h-4 w-4 shrink-0" />
              {t("driver.cancelledTitle")}
            </p>
            <p className="mt-1 text-[12.5px] leading-snug text-[#8A4342]">
              {t("driver.cancelledBody")}
            </p>
          </div>
        )}

        {/* ── Not yet this driver's box ────────────────────────────────────
            Above the frame for the same reason the cancellation notice is: it
            has to be read before anything else on the screen, because it is the
            explanation for everything the screen is not offering. Reached only
            by a link or a back button — the list will not open an out-of-turn
            container — so the driver arriving here believes this is their next
            job, and the banner's work is to name the one that actually is and
            offer a way to it. */}
        {queueLock && (
          <div className="rounded-[14px] border border-[#D8D6CE] bg-[#F5F4F0] px-3 py-3">
            <p className="flex items-center gap-2 text-[13px] font-semibold text-[#44433E]">
              <Lock className="h-4 w-4 shrink-0" />
              {t("driver.queueLock.title")}
            </p>
            <p className="mt-1 text-[12.5px] leading-snug text-[#6B6A64]">
              {t("driver.queueLock.body", {
                container:
                  queueLock.blockedBy.lineNumber ||
                  queueLock.blockedBy.containerNumber ||
                  "",
              })}
            </p>
            <button
              type="button"
              onClick={() =>
                navigate(`/driver/containers/${queueLock.blockedBy._id}`)
              }
              className="mt-2.5 w-full rounded-xl bg-[#EF9F27] px-4 py-3 text-[14px] font-semibold text-white active:bg-[#D98D1C]"
            >
              {t("driver.queueLock.open", {
                container:
                  queueLock.blockedBy.lineNumber ||
                  queueLock.blockedBy.containerNumber ||
                  "",
              })}
            </button>
          </div>
        )}

        {/* ── The stop being worked ──────────────────────────────────────── */}
        {frameShown ? (
          <DriverStopFrame
            line={line}
            stop={currentStop}
            position={position}
            orderNumber={line.orderNumber}
            nextStep={nextStep}
            arrived={arrived}
            busy={busy}
            blockedBy={blockedBy}
            fillableHere={fillableHere}
            onConfirm={recordStep}
            documents={
              lineId && (
                <DriverLineAttachments
                  variant="inline"
                  lineId={lineId}
                  attachments={line.attachments ?? []}
                  onChange={setAttachments}
                  userId={authUser?._id}
                  readonly={line.status === "cancelled"}
                  leading={
                    <DriverCmrTile
                      line={line}
                      direction={direction}
                      countries={countries ?? []}
                    />
                  }
                />
              )
            }
          >
            <DriverContainerDetails
              line={line}
              unlocked={detailsUnlocked}
              readOnly={readOnly}
              numberFrozen={numberFrozen}
              busy={busy}
              missing={missing}
              onSave={saveDetails}
              onScanPlate={() => setCapturing(true)}
            />

            {/* What went into the box, between its figures and its paperwork.
                That is the order the stop actually happens in — read the door,
                check what was loaded, take the paper, drive on — and it is the
                reason this is not a block further down the page: the count is
                agreed at the loading bay, in the same minute as the
                confirmation, by the person watching the forklift.

                Only once the driver has reported in. Before that there is
                nothing to count and the figures above it are locked for the
                same reason. */}
            {detailsUnlocked && cargoPanel}
          </DriverStopFrame>
        ) : (
          /* ── The run is over, and the box is still a record ──────────────
             The figures used to leave with the frame that held them. That was
             an accident of where the panel is mounted rather than a decision:
             the details are a *child* of the stop being worked, so finishing
             the last step took the container number, the seal, the MRN and
             every weight off the screen at once — and a driver reviewing a
             finished run is doing so precisely because somebody has rung them
             about one of those figures. What is left to show is the record, so
             it is shown, read-only and without the two ways in to changing it.

             Read-only regardless of what the status says. A run with every
             step stamped is finished whether or not the stored word has caught
             up, and a form offered over it would be offering a save the API
             refuses. */
          <>
            {finished && (
              <p className="flex items-center gap-2 rounded-2xl bg-[#E9F3DC] px-4 py-3 text-sm font-medium text-[#3B6D11]">
                <Check className="h-4 w-4 shrink-0" />
                {t("driver.allDone")}
              </p>
            )}

            <div className="overflow-hidden rounded-[14px] border border-[#E4E2DA] bg-white">
              <p className="px-3 pt-2.5 text-[13px] font-semibold text-[#1A1A18]">
                {t("driver.detailsTitle")}
              </p>
              {/* The MRN is a row inside the panel here, and only here. It is
                  normally carried by the stop frame above it and left out of
                  the panel as a duplicate — but there is no frame in this
                  state, so the duplicate is the only copy. */}
              <div className="px-1 pb-1">
                <DriverContainerDetails
                  line={line}
                  unlocked
                  readOnly
                  showMrn
                  numberFrozen={numberFrozen}
                  busy={busy}
                  missing={[]}
                  onSave={saveDetails}
                  onScanPlate={() => setCapturing(true)}
                />
              </div>
            </div>

            {/* The same panel, in the block that stands in for the frame.
                A finished run is still asked what was in the box — that is
                most of what the office rings about — and a locked container
                has to show it for the driver to recognise which box they are
                looking at. Read-only in both, because `readOnly` already says
                so. */}
            <div className="overflow-hidden rounded-[14px] border border-[#E4E2DA] bg-white">
              <div className="px-1 py-1">{cargoPanel}</div>
            </div>
          </>
        )}

        {/* ── The rest of the route ──────────────────────────────────────── */}
        <DriverRouteTimeline line={line} currentStopIndex={currentStopIndex} />

        {/* ── And the rest of the day ──────────────────────────────────────
            Directly under the timeline, because it is the question that comes
            straight after the one the timeline answers: how much of this box is
            left, and then what follows it. Both are reference and neither
            records anything, which is why they sit together below the frame.

            Drawn whether or not there is anything next — see the component.

            The gap below it belongs to whichever block is last on the page,
            and that is this one only while a stop is being worked — a
            completed container puts the standalone documents block under it
            and the gap moves down there. With or without a next card, since
            the card is drawn either way. */}
        <div className={completed ? undefined : "mb-16"}>
          <DriverNextContainer
            next={nextContainerFor(queue, lineId)}
            onOpen={(pick) => navigate(`/driver/containers/${pick._id}`)}
          />
        </div>

        {/* ── Documents, when there is no frame to hold them ────────────────
            The list lives inside the stop frame while a stop is being worked —
            the paper is handed over at the gate, in the same minute as the
            confirmation. Once the run is finished there is no frame, and the
            documents still have to be reachable: the last receipt of a run is
            routinely handed over after the final step is recorded. So the
            standalone block is the fallback, not the default, and the condition
            is exactly the one that decides whether a frame was drawn — anything
            looser puts the same list on screen twice.

            Carrying the page's bottom gap when it is drawn, because then it is
            the block the tab bar would otherwise cover. */}
        {lineId && !frameShown && (
          <div className="mb-16">
            <DriverLineAttachments
              lineId={lineId}
              attachments={line.attachments ?? []}
              onChange={setAttachments}
              userId={authUser?._id}
              /* Readable, not writable, on a box that is not this driver's turn.
                 Filing a gate receipt against a container nobody has started is
                 filing it against the wrong container — and the paperwork would
                 still be there to file once the box in front has been
                 finished. */
              readonly={line.status === "cancelled" || !!queueLock}
              leading={
                <DriverCmrTile
                  line={line}
                  direction={direction}
                  countries={countries ?? []}
                />
              }
            />
          </div>
        )}
      </div>
      )}

      {/* ── The conversation about this container ────────────────────────────
          Mounted only while its tab is open, which is what keeps a driver with
          twelve containers on their truck from holding twelve conversations
          they are not reading. The component joins the container's socket room
          for exactly as long as it is on screen, so the words are delivered
          here and the badge above is all that reaches the other eleven. */}
      {tab === "messages" && lineId && (
        <DriverMessagesTab
          lineId={lineId}
          open
          cancelled={line.status === "cancelled"}
          onMessaging={applyMessaging}
        />
      )}

      {/* ── The run is over — and what follows it ────────────────────────────
          A sheet rather than a toast, for the same reason the location panel
          below is one: it asks a question, and a toast disappears while it is
          still being read. It is the only interruption on this screen that is
          not about a failure, which is deliberate — finishing a container is
          the one moment a driver is deciding what to do next, and the answer
          used to be two screens away.

          Both outcomes are offered, and the empty one is not a lesser version:
          "that was your last container" is a fact a driver planning their way
          home actively wants, and a sheet that only appeared when there was
          more work would leave them inferring it from silence. */}
      {justFinished && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="pb-safe w-full max-w-md rounded-t-2xl bg-white p-4 pb-6">
            <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-[#3B6D11]">
              <Check className="h-4 w-4 shrink-0" />
              {t("driver.finishedTitle", { container: line.lineNumber })}
            </p>
            <p className="mb-3 text-[13px] leading-snug text-[#6B6A64]">
              {t(
                nextContainerFor(queue, lineId)
                  ? "driver.finishedNext"
                  : "driver.finishedLast",
              )}
            </p>

            <div className="mb-3">
              <DriverNextContainer
                variant="sheet"
                next={nextContainerFor(queue, lineId)}
                onOpen={(pick) => {
                  setJustFinished(false);
                  navigate(`/driver/containers/${pick._id}`);
                }}
              />
            </div>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() =>
                  navigate("/driver/containers", {
                    state: { tab: "active" },
                  })
                }
                className="w-full rounded-xl border border-[#EF9F27] px-4 py-3 text-[14px] font-medium text-[#EF9F27]"
              >
                {t("driver.backToList")}
              </button>
              {/* Staying put is a real choice: the last document of a run is
                  routinely handed over after the final step is recorded, and
                  the documents block is on the screen behind this. */}
              <button
                type="button"
                onClick={() => setJustFinished(false)}
                className="w-full px-4 py-2 text-[13px] text-[#A6A49C]"
              >
                {t("driver.stayHere")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── No location, and what to do about it ─────────────────────────────
          A sheet rather than a toast, because it needs an answer and a toast
          disappears while the driver is still reading it. Two ways out and no
          third: try again, or ask the office to record the step. There is
          deliberately no "record it anyway" — a step somebody waved through is
          worth less than one the office signed for, and offering the button
          would make the location requirement a suggestion. */}
      {geoBlock && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="pb-safe w-full max-w-md rounded-t-2xl bg-white p-4 pb-6">
            <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-[#1A1A18]">
              <MapPin className="h-4 w-4 shrink-0 text-[#A63A39]" />
              {t("driver.geo.title")}
            </p>
            <p className="mb-4 text-[13px] leading-snug text-[#6B6A64]">
              {t(`driver.geo.reason.${geoBlock.reason}`)}
            </p>

            <div className="flex flex-col gap-2">
              {isRetryable(geoBlock.reason) && (
                <button
                  type="button"
                  onClick={() => recordStep(geoBlock.step)}
                  disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#EF9F27] px-4 py-3 text-[14px] font-semibold text-white disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MapPin className="h-4 w-4" />
                  )}
                  {t("driver.geo.retry")}
                </button>
              )}

              <button
                type="button"
                onClick={notifyDispatch}
                disabled={notifying}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#EF9F27] px-4 py-3 text-[14px] font-medium text-[#EF9F27] disabled:opacity-50"
              >
                {notifying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {t("driver.geo.notifyDispatch")}
              </button>

              <button
                type="button"
                onClick={() => setGeoBlock(null)}
                className="w-full px-4 py-2 text-[13px] text-[#A6A49C]"
              >
                {t("lineEdit.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── What the last reading made of the door ───────────────────────────
          One panel for the plate as a whole rather than per-field hints: three
          figures that do not add up are a single finding, not three, and the
          verdict is about the photograph rather than about any one box. */}
      {reading && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="pb-safe max-h-[85dvh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-4 pb-6">
            <p
              className={`mb-2 flex items-start gap-1.5 text-sm font-semibold ${
                reading.verdict === "red"
                  ? "text-[#A63A39]"
                  : reading.verdict === "green"
                    ? "text-[#3B6D11]"
                    : "text-[#854F0B]"
              }`}
            >
              {reading.verdict === "green" ? (
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              {t(`plate.verdict.${reading.verdict}`)}
            </p>

            {/* The reasons, in the platform's own words. They already name the
                figure to look at and what it disagrees with. */}
            <ul className="mb-3 space-y-1">
              {[...reading.errors, ...reading.warnings].map((issue) => (
                <li key={issue.code} className="text-[11px] text-[#854F0B]">
                  {issue.message}
                </li>
              ))}
            </ul>

            {plateRows.length > 0 ? (
              <>
                <ul className="mb-3 space-y-2.5">
                  {plateRows.map((row) => (
                    <li key={row.field}>
                      <div className="mb-1 flex items-center gap-1.5">
                        <span className="text-[10px] uppercase tracking-wide text-[#A6A49C]">
                          {row.label}
                        </span>
                        {row.fromPhoto && (
                          <span className="text-[10px] text-[#3B6D11]">
                            {t("plate.fromPhoto")}
                          </span>
                        )}
                      </div>
                      <input
                        type={row.numeric ? "number" : "text"}
                        min={row.numeric ? 0 : undefined}
                        inputMode={row.numeric ? "numeric" : undefined}
                        value={valueOf(row)}
                        onChange={(e) =>
                          setEdits((current) => ({
                            ...current,
                            [row.field]: row.numeric
                              ? e.target.value
                              : e.target.value.toUpperCase(),
                          }))
                        }
                        className={`h-10 w-full rounded-lg border px-3 text-sm ${
                          row.numeric
                            ? "border-gray-300"
                            : "border-gray-300 font-mono uppercase tracking-wider"
                        } focus:outline-none focus:ring-2 focus:ring-[#EF9F27]`}
                      />
                    </li>
                  ))}
                </ul>

                {importConflicts.length > 0 && (
                  <p className="mb-2 rounded-lg bg-[#FBEDED] px-3 py-2 text-[11px] text-[#A63A39]">
                    {t(
                      `lineWeights.${importConflicts[0].code}`,
                      importConflicts[0].values,
                    )}
                  </p>
                )}

                <button
                  type="button"
                  onClick={importReading}
                  disabled={importBlocked}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#EF9F27] px-4 py-3 text-[14px] font-semibold text-white disabled:opacity-50"
                >
                  {importing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {importConflicts.length
                    ? t("plate.importBlocked")
                    : t("plate.saveOnly", { count: plateRows.length })}
                </button>
              </>
            ) : (
              /* Nothing to take. Say so, and say what to do instead — a panel
                 that only reports a failure leaves the driver looking for an
                 action that is not on screen. */
              <button
                type="button"
                onClick={() => {
                  setReading(null);
                  setCapturing(true);
                }}
                className="w-full rounded-xl border border-[#A63A39] px-4 py-3 text-[14px] font-medium text-[#A63A39]"
              >
                {t("plate.tryAgain")}
              </button>
            )}

            <button
              type="button"
              onClick={() => setReading(null)}
              className="mt-2 w-full px-4 py-2 text-[13px] text-[#A6A49C]"
            >
              {t("lineEdit.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Full-screen, and mounted last so it sits above everything. The camera
          is released whenever this unmounts — a component that leaves a stream
          running is a light on the back of somebody's phone and a flat battery
          by the afternoon. */}
      {capturing && (
        <PlateCapture
          busy={readingBusy}
          onCapture={readPlate}
          onClose={() => setCapturing(false)}
        />
      )}

      {/* ── The two halves of a container, in the thumb's reach ──────────────
          Fixed to the bottom rather than sitting at the top with the container
          number, because both of these are used one-handed while standing at a
          vehicle — and the bottom of a phone is the only part of it a thumb
          reaches without regripping.

          The badge is the whole reason the conversation is allowed to be
          hidden. A driver working the order tab is told that something is
          waiting without being shown it and without a single message being sent
          to this device — the count comes off the container's own counters,
          which the truck's socket room keeps current. */}
      {/* Hidden while the keyboard is up: the bar is `fixed`, so on iOS it is
          anchored to the layout viewport the keyboard does not shrink — it ends
          up behind the keyboard still occupying the space the composer needs.
          `pb-safe` lifts the buttons off the home indicator. */}
      {!keyboardOpen && (
      <nav
        ref={setTabBar}
        className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-[#E4E2DA] bg-white"
      >
        <div className="mx-auto flex max-w-md">
          {(
            [
              { key: "order", icon: ClipboardList, badge: 0 },
              {
                key: "messages",
                icon: MessageCircle,
                badge: line.messaging?.unreadForDriver ?? 0,
              },
            ] as const
          ).map(({ key, icon: Icon, badge }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                aria-current={active ? "page" : undefined}
                className={`relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11.5px] font-medium transition-colors ${
                  active ? "text-[#B4711F]" : "text-[#8A9590]"
                }`}
              >
                <span className="relative">
                  <Icon className="h-5 w-5" />
                  {badge > 0 && (
                    <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#C0392B] px-1 text-[10px] font-semibold text-white">
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </span>
                {t(`messages.tabs.${key}`)}
              </button>
            );
          })}
        </div>
      </nav>
      )}
    </div>
  );
}
